import { createContext, useCallback, useMemo, useState, type ReactNode } from 'react';
import { Button, type FlashbarProps } from '@cloudscape-design/components';

export interface AppError {
  code?: string;
  message: string;
  requestId?: string;
  timestamp?: string;
  status?: number;
  fields?: Record<string, string>;
  /** Optional UI context, e.g. "saving user". Helps users describe what they were doing. */
  action?: string;
}

interface NotificationContextValue {
  items: FlashbarProps.MessageDefinition[];
  notify: (item: Omit<FlashbarProps.MessageDefinition, 'id' | 'dismissible' | 'onDismiss'>) => string;
  notifyError: (err: AppError | Error | unknown, action?: string) => string;
  notifySuccess: (header: string, content?: string) => string;
  dismiss: (id: string) => void;
  clear: () => void;
}

export const NotificationContext = createContext<NotificationContextValue>({
  items: [],
  notify: () => '',
  notifyError: () => '',
  notifySuccess: () => '',
  dismiss: () => {},
  clear: () => {},
});

function buildErrorContent(err: AppError): { header: string; content: ReactNode; copy: string } {
  const header = err.action ? `Couldn't ${err.action}` : 'Request failed';
  const lines: string[] = [];
  if (err.message) lines.push(err.message);
  if (err.fields) {
    for (const [k, v] of Object.entries(err.fields)) lines.push(`• ${k}: ${v}`);
  }
  const detailLines: string[] = [];
  if (err.code) detailLines.push(`Code: ${err.code}`);
  if (err.status) detailLines.push(`Status: ${err.status}`);
  if (err.requestId) detailLines.push(`Request ID: ${err.requestId}`);
  if (err.timestamp) detailLines.push(`Time: ${err.timestamp}`);

  const content = (
    <div>
      {lines.map((l, i) => (
        <div key={i}>{l}</div>
      ))}
      {detailLines.length > 0 && (
        <details style={{ marginTop: 8 }}>
          <summary>Troubleshooting details</summary>
          <pre style={{ whiteSpace: 'pre-wrap', margin: '4px 0 0 0', fontSize: 12 }}>
            {detailLines.join('\n')}
          </pre>
        </details>
      )}
    </div>
  );

  const copy = [header, ...lines, '', ...detailLines].filter(Boolean).join('\n');
  return { header, content, copy };
}

export function NotificationProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<FlashbarProps.MessageDefinition[]>([]);

  const dismiss = useCallback((id: string) => {
    setItems((prev) => prev.filter((it) => it.id !== id));
  }, []);

  const notify = useCallback<NotificationContextValue['notify']>((item) => {
    const id = crypto.randomUUID();
    const next: FlashbarProps.MessageDefinition = {
      ...item,
      id,
      dismissible: true,
      onDismiss: () => dismiss(id),
    };
    setItems((prev) => [...prev, next]);
    return id;
  }, [dismiss]);

  const notifySuccess = useCallback<NotificationContextValue['notifySuccess']>(
    (header, content) =>
      notify({
        type: 'success',
        header,
        content,
      }),
    [notify],
  );

  const notifyError = useCallback<NotificationContextValue['notifyError']>(
    (err, action) => {
      let appErr: AppError;
      if (err && typeof err === 'object' && 'message' in err) {
        const resolvedAction = action ?? (err as AppError).action;
        appErr = {
          ...(err as AppError),
          ...(resolvedAction !== undefined && { action: resolvedAction }),
        };
      } else {
        appErr = { message: 'Unknown error', ...(action !== undefined && { action }) };
      }
      const { header, content, copy } = buildErrorContent(appErr);
      return notify({
        type: 'error',
        header,
        content,
        // Flashbar's `action` is a React.ReactNode, not a config object — a
        // plain {type, text, ariaLabel, onClick} descriptor here throws React
        // error #31 ("objects are not valid as a child") the moment this
        // branch actually renders, which is exactly what happened in
        // production once real fetch failures started calling notifyError.
        // Render the button directly instead of describing one.
        action: navigator.clipboard ? (
          <Button onClick={() => navigator.clipboard.writeText(copy)} ariaLabel="Copy error details">
            Copy details
          </Button>
        ) : undefined,
      });
    },
    [notify],
  );

  const value = useMemo<NotificationContextValue>(
    () => ({ items, notify, notifyError, notifySuccess, dismiss, clear: () => setItems([]) }),
    [items, notify, notifyError, notifySuccess, dismiss],
  );

  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>;
}
